/**
 * Robust smooth-scroll to an in-page section.
 *
 * Handles the cases that break naive `scrollIntoView` calls:
 * - the target section is still lazy-mounting (retries on animation frames)
 * - the user prefers reduced motion (falls back to an instant jump)
 * - older mobile Safari without smooth `scrollIntoView` support (manual fallback)
 * - keyboard/screen-reader users (moves focus to the section)
 */

const DEFAULT_TIMEOUT_MS = 3000;

export function prefersReducedMotion(): boolean {
  try {
    return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function supportsSmoothScrollIntoView(): boolean {
  try {
    return typeof document !== "undefined" && "scrollBehavior" in document.documentElement.style;
  } catch {
    return false;
  }
}

function focusSection(el: HTMLElement) {
  if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
  try {
    el.focus({ preventScroll: true });
  } catch {
    /* noop */
  }
}

/** Scroll to an element immediately (no waiting). Returns false if it is missing. */
export function scrollToElement(el: HTMLElement | null, smooth = true): boolean {
  if (!el) return false;
  const behavior: ScrollBehavior = smooth && !prefersReducedMotion() ? "smooth" : "auto";

  if (supportsSmoothScrollIntoView()) {
    el.scrollIntoView({ behavior, block: "start" });
  } else {
    // iOS Safari < 15.4 fallback
    const top = el.getBoundingClientRect().top + (window.pageYOffset || window.scrollY || 0);
    window.scrollTo(0, Math.max(0, top));
  }
  focusSection(el);
  return true;
}

/**
 * Scroll to `#id`, retrying until it exists (lazy sections) or the timeout elapses.
 * Returns a promise resolving to whether the element was found.
 */
export function scrollToSection(id: string, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<boolean> {
  const targetId = id.replace(/^#/, "");
  if (!targetId || typeof document === "undefined") return Promise.resolve(false);

  const immediate = document.getElementById(targetId);
  if (immediate) return Promise.resolve(scrollToElement(immediate));

  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const el = document.getElementById(targetId);
      if (el) {
        resolve(scrollToElement(el));
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        resolve(false);
        return;
      }
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(tick);
      } else {
        window.setTimeout(tick, 60);
      }
    };
    tick();
  });
}
